/**
 * 🥣 **THE JATA — the sheep's manger (T286).** Its SHAPE, as boxes.
 *
 * 🎯 **haugur, 2026-10-01, from four candidates (`Design/samples/157`):** *"A,
 * smooth hay."* **The hellujata**: 📖 at Þórarinsstaðir, 11th century, **flat
 * stone slabs set round the walls** made the manger, and a **jötufjöl** — a board
 * along the edge — kept the hay in. **Búalög gives 29 cm of manger a sheep**, so a
 * one-metre jata feeds three at once.
 *
 * 🌾 **Smooth hay**: the level is one of {@link JATA_FILL_STEPS} steps (a block
 * state each), rising flat to the rim and then HEAPING over the last part —
 * a manger you have just filled is piled, not level.
 *
 * 📐 **Boxes in one block cell**, metres, `x` along the manger, `y` up, `z`
 * across it, the cell running 0 … 1 each way, **the board on the +z side** —
 * {@link jataBoxes} turns it to face the way it was placed. Pure data: no
 * three.js here, so it can be tested and drawn by anything.
 */

import { JATA_FILL_STEPS } from '@lofi/shared';

/** What a box is made of — each one maps to a texture the game already has. */
export type JataStuff = 'stone' | 'wood' | 'hay';

export interface JataBox {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
  readonly stuff: JataStuff;
}

/** Which way the board faces — the side a sheep eats from. */
export type JataFacing = 'north' | 'south' | 'east' | 'west';

const box = (
  stuff: JataStuff,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
): JataBox => ({ min: [x0, y0, z0], max: [x1, y1, z1], stuff });

/** 📏 A slab's thickness, and the rim — the manger stands 0.42 m. */
const SLAB = 0.07;
export const JATA_RIM = 0.42;

/** The stone and the board, facing +z. */
const PARTS: readonly JataBox[] = [
  box('stone', 0.04, 0, 0.18, 0.96, 0.06, 0.82), // the floor slab
  box('stone', 0.04, 0, 0.18, 0.96, JATA_RIM, 0.18 + SLAB), // back
  box('stone', 0.04, 0, 0.82 - SLAB, 0.96, JATA_RIM - 0.04, 0.82), // front, a little lower
  box('stone', 0.04, 0, 0.18, 0.04 + SLAB, JATA_RIM, 0.82), // ends
  box('stone', 0.96 - SLAB, 0, 0.18, 0.96, JATA_RIM, 0.82),
  /* the jötufjöl — the board along the front edge */
  box('wood', 0.02, JATA_RIM - 0.06, 0.8, 0.98, JATA_RIM + 0.04, 0.86),
];

/** Where the hay lies. */
const HOLD = {
  x0: 0.04 + SLAB,
  x1: 0.96 - SLAB,
  z0: 0.18 + SLAB,
  z1: 0.82 - SLAB,
  floor: 0.06,
  rim: JATA_RIM - 0.04,
};

/** 🌾 The share of the steps spent filling to the rim; the rest heaps. */
const LEVEL_SHARE = 0.8;
/** 🌾 How high the heap stands over the rim when the jata is full. */
const HEAP = 0.1;

/** 🌾 The hay at one fill step, 0 … {@link JATA_FILL_STEPS}. */
function hay(step: number): JataBox[] {
  const f = Math.min(1, Math.max(0, step / JATA_FILL_STEPS));
  if (f === 0) return [];
  const depth = HOLD.rim - HOLD.floor;
  const level = HOLD.floor + depth * Math.min(1, f / LEVEL_SHARE);
  const out = [box('hay', HOLD.x0, HOLD.floor, HOLD.z0, HOLD.x1, level, HOLD.z1)];
  if (f > LEVEL_SHARE) {
    const heap = HEAP * ((f - LEVEL_SHARE) / (1 - LEVEL_SHARE));
    const inset = 0.08;
    out.push(
      box(
        'hay',
        HOLD.x0 + inset,
        HOLD.rim,
        HOLD.z0 + inset,
        HOLD.x1 - inset,
        HOLD.rim + heap,
        HOLD.z1 - inset,
      ),
    );
  }
  return out;
}

/**
 * 🧭 Turn a box about the cell's centre so the board faces `facing`.
 *
 * ⚠️ **+z is SOUTH in this world** (north is −z — `groundat.mjs`'s trap: the
 * world's z is the negative of the northing), so the unturned shape faces south.
 */
const QUARTERS: ReadonlyMap<JataFacing, number> = new Map([
  ['south', 0],
  ['west', 1],
  ['north', 2],
  ['east', 3],
]);

function turned(b: JataBox, facing: JataFacing): JataBox {
  const quarter = QUARTERS.get(facing) ?? 0;
  const [, y0] = b.min;
  const [, y1] = b.max;
  let [x0, , z0] = b.min;
  let [x1, , z1] = b.max;
  for (let i = 0; i < quarter; i++) {
    /* A quarter turn about (0.5, 0.5): (x, z) → (1 − z, x). */
    [x0, z0, x1, z1] = [1 - z1, x0, 1 - z0, x1];
  }
  return { min: [x0, y0, z0], max: [x1, y1, z1], stuff: b.stuff };
}

/** 🥣 Every box of a jata at one fill step, facing the way it was placed. */
export function jataBoxes(step: number, facing: JataFacing = 'south'): JataBox[] {
  return [...PARTS, ...hay(step)].map((b) => turned(b, facing));
}
